import { Maybe } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import { getSuggestionFormattedLabel } from 'utils/suggestions/suggestionFormat';

import { formatFieldValue } from './suggestionFieldGrouping';
import {
  KitPreviewSection,
  stripPreviewSectionLabelPrefix,
} from './suggestionPreviewConfig';

const HEADER_ONLY_SUFFIXES = new Set([
  'name',
  'addressLine1',
  'city',
  'state',
  'zipCode',
  'zip',
  'country',
]);

const SCHEDULING_SUFFIXES = new Set([
  'apptType',
  'apptStartTime',
  'apptEndTime',
  'apptNote',
  'readyTime',
  'mustDeliver',
]);

const SCHEDULING_SUFFIX_ORDER: Record<string, number> = {
  apptType: 0,
  apptStartTime: 1,
  apptEndTime: 2,
  apptNote: 3,
  readyTime: 4,
  mustDeliver: 5,
};

export const LOAD_BUILDING_LOCATION_SECTIONS = new Set<KitPreviewSection>([
  KitPreviewSection.Pickup,
  KitPreviewSection.Consignee,
  KitPreviewSection.Customer,
]);

export type LoadBuildingLocationField = {
  key: string;
  label: string;
  value: string;
};

export type LoadBuildingLocationPreview = {
  headerLines: string[];
  schedulingFields: LoadBuildingLocationField[];
  bottomFields: LoadBuildingLocationField[];
};

export function isLoadBuildingLocationSection(
  sectionKey: string
): sectionKey is KitPreviewSection {
  return LOAD_BUILDING_LOCATION_SECTIONS.has(sectionKey as KitPreviewSection);
}

function resolvePrefixes(sectionKey: string): string[] {
  if (sectionKey === KitPreviewSection.Consignee) {
    return ['consignee', 'dropoff'];
  }
  return [sectionKey];
}

function getFieldSuffix(key: string, prefixes: string[]): Maybe<string> {
  const prefix = prefixes.find((p) => key.startsWith(`${p}.`));
  if (!prefix) {
    return null;
  }
  return key.slice(prefix.length + 1);
}

function isHiddenFieldSuffix(suffix: string): boolean {
  return suffix.endsWith('zone') || suffix === 'timezone';
}

function isSchedulingFieldSuffix(suffix: string): boolean {
  return SCHEDULING_SUFFIXES.has(suffix) || suffix.startsWith('appt');
}

function readField(
  flattenedChanges: Record<string, unknown>,
  prefixes: string[],
  field: string,
  tmsName: TMS
): Maybe<string> {
  for (const prefix of prefixes) {
    const keys =
      field === 'zipCode'
        ? [`${prefix}.zipCode`, `${prefix}.zip`]
        : [`${prefix}.${field}`];

    for (const key of keys) {
      const val = flattenedChanges[key];
      if (val == null || val === '') {
        continue;
      }
      return formatFieldValue(key, val, flattenedChanges, tmsName);
    }
  }

  return null;
}

function getLocationFieldLabel(
  pipeline: SuggestionPipelines,
  key: string
): Maybe<string> {
  const label = getSuggestionFormattedLabel(pipeline, key);
  if (label) {
    return label;
  }

  if (key.startsWith('dropoff.')) {
    return getSuggestionFormattedLabel(
      pipeline,
      key.replace(/^dropoff\./, 'consignee.')
    );
  }

  return null;
}

function compareSchedulingFields(
  a: LoadBuildingLocationField,
  b: LoadBuildingLocationField
): number {
  const aSuffix = a.key.split('.').pop() ?? '';
  const bSuffix = b.key.split('.').pop() ?? '';
  const aOrder = SCHEDULING_SUFFIX_ORDER[aSuffix] ?? 99;
  const bOrder = SCHEDULING_SUFFIX_ORDER[bSuffix] ?? 99;

  if (aOrder !== bOrder) {
    return aOrder - bOrder;
  }
  return a.key.localeCompare(b.key);
}

function compareBottomFields(
  a: LoadBuildingLocationField,
  b: LoadBuildingLocationField
): number {
  const aIsRef = a.key.endsWith('.refNumber');
  const bIsRef = b.key.endsWith('.refNumber');
  if (aIsRef && !bIsRef) {
    return 1;
  }
  if (!aIsRef && bIsRef) {
    return -1;
  }
  return a.label.localeCompare(b.label);
}

function buildHeaderLines(
  sectionKey: string,
  flattenedChanges: Record<string, unknown>,
  tmsName: TMS
): string[] {
  const prefixes = resolvePrefixes(sectionKey);
  const headerLines: string[] = [];

  const name = readField(flattenedChanges, prefixes, 'name', tmsName);
  if (name) {
    headerLines.push(name);
  }

  const addressLine1 = readField(
    flattenedChanges,
    prefixes,
    'addressLine1',
    tmsName
  );
  if (addressLine1) {
    headerLines.push(addressLine1);
  }

  const city = readField(flattenedChanges, prefixes, 'city', tmsName);
  const state = readField(flattenedChanges, prefixes, 'state', tmsName);
  const zip = readField(flattenedChanges, prefixes, 'zipCode', tmsName);
  const country = readField(flattenedChanges, prefixes, 'country', tmsName);

  const locationParts = [city, state, zip].filter(Boolean) as string[];
  const locationLine = [locationParts.join(', '), country]
    .filter(Boolean)
    .join(', ');
  if (locationLine) {
    headerLines.push(locationLine);
  }

  return headerLines;
}

function collectLocationFields(
  sectionKey: string,
  flattenedChanges: Record<string, unknown>,
  pipeline: SuggestionPipelines,
  tmsName: TMS
): {
  schedulingFields: LoadBuildingLocationField[];
  bottomFields: LoadBuildingLocationField[];
} {
  const prefixes = resolvePrefixes(sectionKey);
  const schedulingFields: LoadBuildingLocationField[] = [];
  const bottomFields: LoadBuildingLocationField[] = [];

  Object.entries(flattenedChanges).forEach(([key, val]) => {
    const suffix = getFieldSuffix(key, prefixes);
    if (!suffix || val == null || val === '') {
      return;
    }
    if (isHiddenFieldSuffix(suffix) || HEADER_ONLY_SUFFIXES.has(suffix)) {
      return;
    }

    const label = getLocationFieldLabel(pipeline, key);
    if (!label) {
      return;
    }

    const field: LoadBuildingLocationField = {
      key,
      label: stripPreviewSectionLabelPrefix(label, sectionKey),
      value: formatFieldValue(key, val, flattenedChanges, tmsName),
    };

    if (isSchedulingFieldSuffix(suffix)) {
      schedulingFields.push(field);
    } else {
      bottomFields.push(field);
    }
  });

  schedulingFields.sort(compareSchedulingFields);
  bottomFields.sort(compareBottomFields);

  return { schedulingFields, bottomFields };
}

// Readable block for pickup / consignee / customer in load-building preview tooltips.
export function buildLoadBuildingLocationSection(
  sectionKey: string,
  flattenedChanges: Record<string, unknown>,
  tmsName: TMS
): LoadBuildingLocationPreview {
  const headerLines = buildHeaderLines(sectionKey, flattenedChanges, tmsName);
  const { schedulingFields, bottomFields } = collectLocationFields(
    sectionKey,
    flattenedChanges,
    SuggestionPipelines.LoadBuilding,
    tmsName
  );

  return { headerLines, schedulingFields, bottomFields };
}

export function hasLoadBuildingLocationContent(
  preview: LoadBuildingLocationPreview
): boolean {
  return (
    preview.headerLines.length > 0 ||
    preview.schedulingFields.length > 0 ||
    preview.bottomFields.length > 0
  );
}

export function loadBuildingLocationSectionHasContent(
  sectionKey: string,
  flattenedChanges: Record<string, unknown>,
  tmsName: TMS
): boolean {
  const preview = buildLoadBuildingLocationSection(
    sectionKey,
    flattenedChanges,
    tmsName
  );
  return hasLoadBuildingLocationContent(preview);
}
